import { ApiError, NetworkError, isRetryable } from './errors';

/**
 * The app's one door to the InGo API (InGo mobile plan §3: "No screen calls
 * `fetch` on its own"). Cut from KangaruRide's `mobile/src/api/client.ts`:
 * the bearer token, InGo's envelope, refusals as `ApiError` with the
 * server's `code`, and "did not arrive" as `NetworkError`.
 *
 * What it adds is retrying, and only where it is safe: a read (GET) that met
 * a dropped connection, a 5xx or a 429 is tried again after a pause; a write
 * never is, because a write whose answer was lost may already have happened
 * (Kangaru ADR-0023 §2). A caller whose write is safe to repeat (it carries an
 * idempotency key the server honours) says so with `retry: true`.
 */

/** 15 s: long enough on a weak upcountry connection, short enough not to stare at a spinner. */
export const DEFAULT_TIMEOUT_MS = 15000;

/** Two more tries after the first: three reads in all. */
export const DEFAULT_MAX_RETRIES = 2;

/** A `Retry-After` longer than this is handed to the person instead of waited out. */
export const MAX_RETRY_AFTER_SECONDS = 30;

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * @param {object} config
 * @param {string | (() => string)} config.baseUrl The API root (with `/api/v1`), or a function that returns it and may throw.
 * @param {() => (string | null | Promise<string | null>)} config.getToken
 * @param {() => void} [config.onUnauthenticated] A 401 UNAUTHENTICATED: the token is dead.
 * @param {() => void} [config.onPasswordChangeRequired] A 403 PASSWORD_CHANGE_REQUIRED.
 */
export function createApiClient({
  baseUrl,
  getToken,
  onUnauthenticated = () => {},
  onPasswordChangeRequired = () => {},
  fetchImpl = (...args) => fetch(...args),
  sleep = defaultSleep,
  random = Math.random,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  maxRetries = DEFAULT_MAX_RETRIES,
}) {
  const rootUrl = () => (typeof baseUrl === 'function' ? baseUrl() : baseUrl);

  async function send(path, options) {
    const url = rootUrl() + path + buildQuery(options.query);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), options.timeoutMs ?? timeoutMs);
    const onCallerAbort = () => controller.abort();

    options.signal?.addEventListener('abort', onCallerAbort);
    if (options.signal?.aborted) {
      controller.abort();
    }

    const token = await getToken();
    const headers = { Accept: 'application/json', ...options.headers };

    // After the spread, deliberately: a caller may add a header but never
    // replace the bearer this client owns.
    delete headers.authorization;
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    } else {
      delete headers.Authorization;
    }

    // JSON only; for multipart the runtime writes the boundary itself.
    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    const body = options.form ?? (options.body === undefined ? undefined : JSON.stringify(options.body));

    try {
      return await fetchImpl(url, {
        method: options.method ?? 'GET',
        headers,
        ...(body === undefined ? {} : { body }),
        signal: controller.signal,
      });
    } catch (cause) {
      throw new NetworkError('The request did not reach InGo.', cause);
    } finally {
      clearTimeout(timer);
      options.signal?.removeEventListener('abort', onCallerAbort);
    }
  }

  async function once(path, options) {
    const response = await send(path, options);

    if (response.status === 204) {
      return { success: true, message: '', data: undefined };
    }

    const payload = await readJson(response);

    if (!response.ok) {
      const error = toApiError(response, payload);

      if (error.status === 401 && error.code === 'UNAUTHENTICATED') {
        onUnauthenticated();
      }

      if (error.status === 403 && error.code === 'PASSWORD_CHANGE_REQUIRED') {
        onPasswordChangeRequired();
      }

      throw error;
    }

    // A 200 that is not InGo's envelope did not come from InGo: a captive
    // portal or a proxy's page. Whether the request reached the API is unknown.
    if (payload === null || typeof payload !== 'object' || payload.success !== true) {
      throw new NetworkError('The answer did not come from InGo.');
    }

    return payload;
  }

  /**
   * @param {string} path Below the root, starting with `/`: `/places/search`.
   * @param {object} [options] `method`, `body` (JSON) or `form` (FormData), `query`, `headers`, `timeoutMs`, `signal`, `retry`.
   * @returns {Promise<{ success: true, message: string, data: any, meta?: any }>}
   */
  async function request(path, options = {}) {
    const method = options.method ?? 'GET';
    const retries = (options.retry ?? method === 'GET') ? maxRetries : 0;

    for (let attempt = 0; ; attempt += 1) {
      try {
        return await once(path, options);
      } catch (error) {
        const delay = attempt < retries && !options.signal?.aborted ? retryDelay(error, attempt, random) : null;

        if (delay === null) {
          throw error;
        }

        await sleep(delay);
      }
    }
  }

  return {
    request,
    get: (path, options = {}) => request(path, { ...options, method: 'GET' }),
    post: (path, body, options = {}) => request(path, { ...options, method: 'POST', body }),
    put: (path, body, options = {}) => request(path, { ...options, method: 'PUT', body }),
    patch: (path, body, options = {}) => request(path, { ...options, method: 'PATCH', body }),
    delete: (path, options = {}) => request(path, { ...options, method: 'DELETE' }),
  };
}

/**
 * How long to wait before the next try, or null for "do not try again".
 * The server's `Retry-After` when it gave one (and it is short enough to wait
 * out); otherwise 0.5 s, then 1 s, each with jitter so a hundred phones coming
 * back from the same dead zone do not arrive in step.
 */
export function retryDelay(error, attempt, random = Math.random) {
  if (!isRetryable(error)) {
    return null;
  }

  if (error instanceof ApiError && error.retryAfterSeconds !== null) {
    return error.retryAfterSeconds <= MAX_RETRY_AFTER_SECONDS ? error.retryAfterSeconds * 1000 : null;
  }

  return Math.round(500 * 2 ** attempt * (0.5 + random() / 2));
}

function buildQuery(query) {
  if (!query) {
    return '';
  }

  const pairs = Object.entries(query).filter(([, value]) => value !== undefined && value !== null);

  if (pairs.length === 0) {
    return '';
  }

  return '?' + pairs.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`).join('&');
}

async function readJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * A refusal as an `ApiError`. The fallbacks matter more than the happy path:
 * a proxy's 502 carries HTML and no `code`, so one is made from the status
 * rather than left undefined for a screen to branch on.
 */
function toApiError(response, payload) {
  const envelope = payload !== null && typeof payload === 'object' ? payload : {};
  const hasCode = typeof envelope.code === 'string';
  const serverMessage = typeof envelope.message === 'string' && envelope.message !== '' ? envelope.message : null;
  const retryAfter = Number.parseInt(response.headers?.get?.('Retry-After') ?? '', 10);

  return new ApiError({
    code: hasCode ? envelope.code : codeForStatus(response.status),
    message: serverMessage ?? 'Something went wrong. Please try again.',
    status: response.status,
    errors: isPlainObject(envelope.errors) ? envelope.errors : {},
    retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter >= 0 ? retryAfter : null,
    serverMessage,
    envelope: hasCode,
    requestId: response.headers?.get?.('X-Request-Id') ?? null,
    meta: isPlainObject(envelope.meta) ? envelope.meta : {},
  });
}

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function codeForStatus(status) {
  if (status === 401) return 'UNAUTHENTICATED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 422) return 'VALIDATION_FAILED';
  if (status === 429) return 'THROTTLED';

  return 'SERVER_ERROR';
}
